import { RadioGroup, type RadioItem } from '@affine/component';
import { registerAffineCommand } from '@affine/core/commands';
import {
  EditorService,
  PresentationStateMachine,
} from '@affine/core/modules/editor';
import { isElsterEnabledFromBody } from '@affine/core/modules/streamlain/hooks/use-elster-gate';
import { FullscreenRegistry } from '@affine/core/modules/streamlain/services/fullscreen-registry';
import { ViewService, WorkbenchService } from '@affine/core/modules/workbench';
import { createIsland } from '@affine/core/utils/island';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { DocMode } from '@blocksuite/affine/model';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  Fragment,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { NavigationRailCollapse } from '../../components/navigation-rail-collapse';
import { DetailPageHeaderModerationButton } from '../block-suite-header/present/detail-header-moderation-button';
import { DetailPageHeaderPresentButton } from '../block-suite-header/present/detail-header-present-button';
import { orderCanonicalDatabaseCandidates } from '../utils/database-canonical-selection';
import { isEditableKeyboardEventTarget } from '../utils/keyboard-event-target';
import { DatabaseFullscreenSwitchIcon } from './database-fullscreen-switch-icon';
import {
  canvasModeActions,
  switchItem,
  verticalSwitchContent,
  verticalSwitchGroup,
  verticalSwitchIndicator,
  verticalSwitchItem,
  verticalSwitchLabel,
  verticalSwitchSubgroup,
} from './style.css';
import {
  EdgelessSwitchItem,
  ElsterSwitchItem,
  FullscreenSwitchItem,
  PageSwitchItem,
} from './switch-items';

export {
  verticalSwitchGroup as railModeGroupStyle,
  verticalSwitchContent as railModeItemContentStyle,
  verticalSwitchIndicator as railModeItemIndicatorStyle,
  verticalSwitchLabel as railModeItemLabelStyle,
  verticalSwitchItem as railModeItemStyle,
} from './style.css';

export interface EditorModeSwitchProps {
  pageId: string;
  isPublic?: boolean;
  publicMode?: DocMode;
}

const createEdgelessRadioItem = (
  tooltipSide: 'right' | 'bottom',
  showTooltip: boolean
): RadioItem => ({
  value: 'edgeless',
  label: (
    <EdgelessSwitchItem showTooltip={showTooltip} tooltipSide={tooltipSide} />
  ),
  testId: 'switch-edgeless-mode-button',
  className: switchItem,
});
const createPageRadioItem = (
  disabled: boolean,
  disabledReason: string | undefined,
  tooltipSide: 'right' | 'bottom',
  showTooltip: boolean
): RadioItem => ({
  value: 'page',
  label: (
    <PageSwitchItem
      disabledReason={disabledReason}
      showTooltip={showTooltip}
      tooltipSide={tooltipSide}
    />
  ),
  testId: 'switch-page-mode-button',
  className: switchItem,
  attrs: disabled
    ? { 'aria-disabled': true, 'data-disabled': 'true' }
    : undefined,
});
// sl-2w9: Fullscreen mode radio item (created dynamically based on disabled state)
const createFullscreenRadioItem = (
  disabled: boolean,
  disabledReason: string | undefined,
  tooltipSide: 'right' | 'bottom',
  showTooltip: boolean
): RadioItem => ({
  value: 'fullscreen',
  label: (
    <FullscreenSwitchItem
      disabledReason={disabledReason}
      showTooltip={showTooltip}
      tooltipSide={tooltipSide}
    />
  ),
  testId: 'switch-fullscreen-mode-button',
  className: switchItem,
  attrs: disabled
    ? { 'aria-disabled': true, 'data-disabled': 'true' }
    : undefined,
});

// sl-yphr.25: ELSTER mode radio item
const ElsterRadioItem: RadioItem = {
  value: 'elster',
  label: <ElsterSwitchItem />,
  testId: 'switch-elster-mode-button',
  className: switchItem,
};

// sl-2w9: Extended mode type to include fullscreen + elster
type ExtendedMode = DocMode | 'fullscreen' | 'elster';

/**
 * sl-2w9: Find the first canvas database in the document.
 * Canvas databases are databases directly on the canvas (not inside notes).
 */
function findFirstCanvasDatabase(doc: any): string | null {
  if (!doc?.root) return null;

  const databases: { id: string; model: unknown }[] = [];

  // Traverse all blocks to find databases
  const traverse = (blockId: string) => {
    const block = doc.getBlock(blockId);
    if (!block) return;

    if (block.flavour === 'affine:database') {
      databases.push({ id: blockId, model: block.model });
    }

    // Traverse children
    const children = block.model.children;
    if (children) {
      children.forEach((child: any) => {
        traverse(child.id);
      });
    }
  };

  if (doc.root) {
    traverse(doc.root.id);
  }

  // Return newest first (reverse order)
  if (databases.length === 0) return null;
  databases.reverse();
  return orderCanonicalDatabaseCandidates(databases)[0]?.id || null;
}

export interface EditorModeSwitchViewProps {
  direction?: 'horizontal' | 'vertical';
  hideElster?: boolean;
}

export const EditorModeSwitch = ({
  direction = 'horizontal',
  hideElster = false,
}: EditorModeSwitchViewProps = {}) => {
  const t = useI18n();
  const editor = useService(EditorService).editor;
  const presentationStateMachine = useService(PresentationStateMachine);
  const isModerating = useLiveData(presentationStateMachine.isModerating$);
  const trash = useLiveData(editor.doc.trash$);
  const isSharedMode = editor.isSharedMode;
  const currentMode = useLiveData(editor.mode$);
  const fullscreenRegistry = useServiceOptional(FullscreenRegistry);
  const activeFullscreenView = useLiveData(
    fullscreenRegistry?.activeView$ ?? null
  );
  const isDatabaseFullscreen = activeFullscreenView?.viewId === 'database';
  const view = useServiceOptional(ViewService)?.view;
  const workbench = useServiceOptional(WorkbenchService)?.workbench;
  const activeView = useLiveData(workbench?.activeView$);
  const isActiveView = activeView?.id && activeView?.id === view?.id;
  const [compactRail, setCompactRail] = useState(false);

  useEffect(() => {
    if (direction !== 'vertical' || typeof window === 'undefined') {
      setCompactRail(false);
      return;
    }
    const mediaQuery = window.matchMedia('screen and (max-width: 900px)');
    const updateCompactRail = () => setCompactRail(mediaQuery.matches);
    updateCompactRail();
    mediaQuery.addEventListener('change', updateCompactRail);
    return () => mediaQuery.removeEventListener('change', updateCompactRail);
  }, [direction]);

  // sl-2w9: Find first canvas database for fullscreen button (reactive)
  const doc = editor.doc.blockSuiteDoc;
  const [firstCanvasDatabaseId, setFirstCanvasDatabaseId] = useState<
    string | null
  >(() => findFirstCanvasDatabase(doc));

  // Subscribe to block changes to reactively update the database detection
  useEffect(() => {
    if (!doc) return;

    const updateDatabaseId = () => {
      setFirstCanvasDatabaseId(findFirstCanvasDatabase(doc));
    };

    // Initial check
    updateDatabaseId();

    // Subscribe to root changes
    const rootSub = doc.slots.rootAdded.subscribe(updateDatabaseId);

    // Subscribe to block updates (add/remove)
    const blockSub = doc.slots.blockUpdated.subscribe(({ type }) => {
      if (type === 'add' || type === 'delete') {
        updateDatabaseId();
      }
    });

    return () => {
      rootSub.unsubscribe();
      blockSub.unsubscribe();
    };
  }, [doc]);

  // sl-yphr.25: Detect if doc has affine:elster block (= ELSTER doc)
  const [hasElsterBlock, setHasElsterBlock] = useState(false);
  useEffect(() => {
    if (!doc) return;

    const checkElster = () => {
      const children = doc.root?.children ?? [];
      setHasElsterBlock(
        children.some((c: { flavour: string }) => c.flavour === 'affine:elster')
      );
    };

    checkElster();

    const rootSub = doc.slots.rootAdded.subscribe(checkElster);
    const blockSub = doc.slots.blockUpdated.subscribe(
      ({ type }: { type: string }) => {
        if (type === 'add' || type === 'delete') checkElster();
      }
    );

    return () => {
      rootSub.unsubscribe();
      blockSub.unsubscribe();
    };
  }, [doc]);

  // sl-yphr.25: Show ELSTER button only when doc has elster block AND gate is enabled
  const showElsterButton = hasElsterBlock && isElsterEnabledFromBody();

  // sl-2w9: Determine effective mode (fullscreen or elster overlay)
  const effectiveMode: ExtendedMode =
    activeFullscreenView?.viewId === 'elster'
      ? 'elster'
      : isDatabaseFullscreen
        ? 'fullscreen'
        : (currentMode ?? 'page');

  const togglePage = useCallback(() => {
    if (isModerating || isSharedMode || trash) return;
    if (activeFullscreenView) fullscreenRegistry?.closeByUser();
    editor.setMode('page', false);
    editor.persistMode(); // explicit user action → save to localStorage
    editor.setSelector(undefined);
    track.$.header.actions.switchPageMode({ mode: 'page' });
  }, [
    editor,
    isModerating,
    isSharedMode,
    trash,
    activeFullscreenView,
    fullscreenRegistry,
  ]);

  const toggleEdgeless = useCallback(() => {
    if (isSharedMode || trash) return;
    if (activeFullscreenView) fullscreenRegistry?.closeByUser();
    editor.setMode('edgeless', false);
    editor.persistMode(); // explicit user action → save to localStorage
    editor.setSelector(undefined);
    track.$.header.actions.switchPageMode({ mode: 'edgeless' });
  }, [editor, isSharedMode, trash, activeFullscreenView, fullscreenRegistry]);

  // sl-a7z: Toggle fullscreen mode (shows placeholder if no DB)
  const toggleFullscreen = useCallback(() => {
    if (isModerating) return;
    if (isDatabaseFullscreen) {
      // Already in fullscreen, close it (user action → clear DocViewState)
      fullscreenRegistry?.closeByUser();
    } else {
      // Close other fullscreen if active
      if (activeFullscreenView) fullscreenRegistry?.closeByUser();
      // Switch to edgeless if needed, then open fullscreen
      if (currentMode === 'page') {
        editor.setMode('edgeless', false);
      }
      // sl-a7z: Use placeholder ID if no database exists
      const dbId = firstCanvasDatabaseId || '__placeholder__';
      fullscreenRegistry?.open('database', dbId);
    }
  }, [
    editor,
    currentMode,
    isDatabaseFullscreen,
    firstCanvasDatabaseId,
    fullscreenRegistry,
    activeFullscreenView,
    isModerating,
  ]);

  // sl-yphr.25: Toggle ELSTER fullscreen via FullscreenRegistry
  const toggleElster = useCallback(() => {
    if (!fullscreenRegistry || !doc) return;
    if (activeFullscreenView?.viewId === 'elster') {
      fullscreenRegistry.closeByUser();
    } else {
      // Close any active fullscreen
      if (activeFullscreenView) fullscreenRegistry.closeByUser();
      // Find the elster block
      const children = doc.root?.children ?? [];
      const elsterBlock = children.find(
        (c: { id: string; flavour: string }) => c.flavour === 'affine:elster'
      );
      if (elsterBlock) {
        if (currentMode === 'page') {
          editor.setMode('edgeless', false);
        }
        fullscreenRegistry.open('elster', elsterBlock.id);
      }
    }
  }, [fullscreenRegistry, doc, activeFullscreenView, editor, currentMode]);

  const onModeChange = useCallback(
    (mode: ExtendedMode) => {
      if (isModerating && (mode === 'page' || mode === 'fullscreen')) {
        return;
      }
      if (mode === 'page') {
        if (activeFullscreenView) fullscreenRegistry?.closeByUser();
        editor.closeWorkflowFullscreen();
        togglePage();
      } else if (mode === 'edgeless') {
        if (activeFullscreenView) fullscreenRegistry?.closeByUser();
        editor.closeWorkflowFullscreen();
        toggleEdgeless();
      } else if (mode === 'fullscreen') {
        editor.closeWorkflowFullscreen();
        toggleFullscreen();
      } else if (mode === 'elster') {
        toggleElster();
      }
    },
    [
      toggleEdgeless,
      togglePage,
      toggleFullscreen,
      toggleElster,
      activeFullscreenView,
      fullscreenRegistry,
      editor,
      isModerating,
    ]
  );

  const shouldHide = useCallback(
    (mode: DocMode) => (trash || isSharedMode) && currentMode !== mode,
    [currentMode, isSharedMode, trash]
  );

  // sl-df0: Direct keydown listener for Alt+Q/E/D
  // Uses event.code which fires before character insertion
  useEffect(() => {
    if (trash || isSharedMode || !isActiveView) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle Alt/Option key combinations (no other modifiers)
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      if (isEditableKeyboardEventTarget(event)) {
        return;
      }

      if (event.code === 'KeyQ') {
        event.preventDefault();
        event.stopPropagation();
        onModeChange('page');
      } else if (event.code === 'KeyE') {
        event.preventDefault();
        event.stopPropagation();
        onModeChange('edgeless');
      } else if (event.code === 'KeyD') {
        event.preventDefault();
        event.stopPropagation();
        onModeChange('fullscreen');
      } else if (event.code === 'KeyT' && showElsterButton) {
        event.preventDefault();
        event.stopPropagation();
        onModeChange('elster');
      }
    };

    // Use capture phase to intercept before blocksuite keyboard handlers
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    currentMode,
    editor,
    isActiveView,
    isSharedMode,
    onModeChange,
    showElsterButton,
    trash,
  ]);

  // sl-a7z: Register command for command palette (no keybinding - handled by keydown listener above)
  useEffect(() => {
    if (trash || isSharedMode || !isActiveView || isModerating) return;
    return registerAffineCommand({
      id: 'affine:toggle-database-fullscreen',
      category: 'editor:page',
      label: t['com.streamlain.header.mode-switch.database'](),
      icon: <DatabaseFullscreenSwitchIcon />,
      // Note: keybinding handled by direct keydown listener for Mac compatibility
      run: () => onModeChange('fullscreen'),
    });
  }, [isActiveView, isModerating, isSharedMode, onModeChange, t, trash]);

  const moderationModeDisabledReason = isModerating
    ? t['com.streamlain.moderation.modeSwitchDisabled']()
    : undefined;

  return (
    <PureEditorModeSwitch
      mode={effectiveMode}
      setMode={onModeChange}
      hidePage={shouldHide('page')}
      hideEdgeless={shouldHide('edgeless')}
      disablePage={isModerating}
      disableFullscreen={isModerating}
      disabledReason={moderationModeDisabledReason}
      showElster={showElsterButton && !hideElster}
      direction={direction}
      showTooltips={direction !== 'vertical' || compactRail}
      canvasActions={
        direction === 'vertical' ? (
          <NavigationRailCollapse expanded={effectiveMode === 'edgeless'}>
            <div className={canvasModeActions}>
              <DetailPageHeaderPresentButton
                rail
                railLabel={t['com.streamlain.share-page.header.present']()}
                showTooltip={compactRail}
              />
              <DetailPageHeaderModerationButton
                rail
                railLabel={t['com.streamlain.moderation.button.label']()}
                renderOutsideEdgeless
                showTooltip={compactRail}
              />
            </div>
          </NavigationRailCollapse>
        ) : undefined
      }
    />
  );
};

export interface PureEditorModeSwitchProps {
  direction?: 'horizontal' | 'vertical';
  mode?: ExtendedMode;
  setMode?: (mode: ExtendedMode) => void;
  hidePage?: boolean;
  hideEdgeless?: boolean;
  disablePage?: boolean;
  disableFullscreen?: boolean;
  disabledReason?: string;
  canvasActions?: ReactNode;
  /** Hide the fullscreen button completely (e.g., for history preview) */
  hideFullscreen?: boolean;
  /** sl-yphr.25: Show ELSTER mode button (only for ELSTER docs) */
  showElster?: boolean;
  showTooltips?: boolean;
}

export const PureEditorModeSwitch = ({
  direction = 'horizontal',
  mode,
  setMode,
  hidePage,
  hideEdgeless,
  disablePage = false,
  disableFullscreen = false,
  disabledReason,
  canvasActions,
  hideFullscreen = false,
  showElster = false,
  showTooltips = true,
}: PureEditorModeSwitchProps) => {
  const t = useI18n();
  const groupRef = useRef<HTMLDivElement>(null);
  const [hasTruncatedLabel, setHasTruncatedLabel] = useState(false);
  const tooltipSide = direction === 'vertical' ? 'right' : 'bottom';
  const displayTooltips = showTooltips || hasTruncatedLabel;
  const modeLabels: Record<ExtendedMode, string> = {
    page: t['com.streamlain.pageMode.page'](),
    edgeless: t['com.streamlain.quicksearch.sourceType.edgeless'](),
    fullscreen: t['com.streamlain.quicksearch.sourceType.database'](),
    elster: 'ELSTER',
  };

  useEffect(() => {
    const group = groupRef.current;
    if (direction !== 'vertical' || !group) {
      setHasTruncatedLabel(false);
      return;
    }
    const measureLabels = () => {
      const labels = group.querySelectorAll<HTMLElement>('[data-mode-label]');
      setHasTruncatedLabel(
        Array.from(labels).some(label => label.scrollWidth > label.clientWidth)
      );
    };
    measureLabels();
    const observer = new ResizeObserver(measureLabels);
    observer.observe(group);
    return () => observer.disconnect();
  }, [direction, modeLabels.edgeless, modeLabels.fullscreen, modeLabels.page]);
  // sl-a7z: Always allow fullscreen mode change (shows placeholder if no DB)
  const handleModeChange = useCallback(
    (newMode: ExtendedMode) => {
      setMode?.(newMode);
    },
    [setMode]
  );

  const handleAvailableModeChange = useCallback(
    (newMode: ExtendedMode) => {
      if (
        (newMode === 'page' && disablePage) ||
        (newMode === 'fullscreen' && disableFullscreen)
      ) {
        return;
      }
      handleModeChange(newMode);
    },
    [disableFullscreen, disablePage, handleModeChange]
  );

  const handleModeClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const item = target?.closest('[data-testid]') as HTMLElement | null;
      if (!item || !mode) return;

      const testId = item.getAttribute('data-testid');
      const clickedMode: ExtendedMode | null =
        testId === 'switch-page-mode-button'
          ? 'page'
          : testId === 'switch-edgeless-mode-button'
            ? 'edgeless'
            : testId === 'switch-fullscreen-mode-button'
              ? 'fullscreen'
              : testId === 'switch-elster-mode-button'
                ? 'elster'
                : null;
      if (clickedMode && clickedMode === mode) {
        handleAvailableModeChange(clickedMode);
      }
    },
    [handleAvailableModeChange, mode]
  );

  const items = useMemo(
    () => [
      ...(hidePage
        ? []
        : [
            createPageRadioItem(
              disablePage,
              disablePage ? disabledReason : undefined,
              tooltipSide,
              displayTooltips
            ),
          ]),
      ...(hideEdgeless
        ? []
        : [createEdgelessRadioItem(tooltipSide, displayTooltips)]),
      // sl-a7z: Always show fullscreen button, never disabled
      ...(hideFullscreen
        ? []
        : [
            createFullscreenRadioItem(
              disableFullscreen,
              disableFullscreen ? disabledReason : undefined,
              tooltipSide,
              displayTooltips
            ),
          ]),
      // sl-yphr.25: ELSTER mode button (only for ELSTER docs when enabled)
      ...(showElster ? [ElsterRadioItem] : []),
    ],
    [
      disableFullscreen,
      disablePage,
      disabledReason,
      hideEdgeless,
      hidePage,
      hideFullscreen,
      showElster,
      displayTooltips,
      tooltipSide,
    ]
  );
  return (
    <div ref={groupRef} onClickCapture={handleModeClickCapture}>
      {direction === 'vertical' ? (
        <RadixRadioGroup.Root
          className={verticalSwitchGroup}
          value={mode}
          onValueChange={handleAvailableModeChange}
          orientation="vertical"
        >
          {items.map(item => {
            const radioItem = (
              <RadixRadioGroup.Item
                value={item.value}
                className={clsx(verticalSwitchItem, item.className)}
                style={item.style}
                data-testid={item.testId}
                {...item.attrs}
              >
                <RadixRadioGroup.Indicator
                  forceMount
                  className={verticalSwitchIndicator}
                />
                <span className={verticalSwitchContent}>{item.label}</span>
                <span className={verticalSwitchLabel} data-mode-label>
                  {modeLabels[item.value as ExtendedMode]}
                </span>
              </RadixRadioGroup.Item>
            );

            return item.value === 'edgeless' && canvasActions ? (
              <div
                className={verticalSwitchSubgroup}
                data-active={mode === 'edgeless'}
                key={item.value}
              >
                {radioItem}
                {canvasActions}
              </div>
            ) : (
              <Fragment key={item.value}>{radioItem}</Fragment>
            );
          })}
        </RadixRadioGroup.Root>
      ) : (
        <RadioGroup
          iconMode
          itemHeight={24}
          borderRadius={8}
          padding={4}
          gap={8}
          value={mode}
          items={items}
          onChange={handleAvailableModeChange}
        />
      )}
    </div>
  );
};

const documentRailModeIsland = createIsland();

export const DocumentRailModeTarget = documentRailModeIsland.Target;

export const ActiveDocumentRailModeProvider = (
  props: HTMLAttributes<HTMLDivElement>
) => {
  const view = useService(ViewService).view;
  const workbench = useService(WorkbenchService).workbench;
  const activeView = useLiveData(workbench.activeView$);

  if (activeView?.id !== view.id) {
    return null;
  }

  return (
    <documentRailModeIsland.Provider>
      <div data-navigation-rail-content="true" {...props}>
        <EditorModeSwitch direction="vertical" hideElster />
      </div>
    </documentRailModeIsland.Provider>
  );
};
